/**
 * Audio toolbox — low-level Web Audio primitives every variant builds on.
 *
 * This file deliberately contains **no finished sounds**. It exposes the
 * shared `AudioContext` singleton, helpers for the building blocks (oscillator,
 * envelope, noise, chaining), and a mute gate. Variants compose these into
 * their own bespoke SFX palettes (see `variants/<variant>/sound.ts`).
 *
 * Design rules:
 *  - One shared `AudioContext`. Lazy. Unlocked on first user gesture.
 *  - Master gain stays in this module — variants connect into it via
 *    `getMasterGain()` (or simply call `getAudioContext()` and route to
 *    `ctx.destination` directly if they want their own bus).
 *  - `playIfNotMuted` is the one thing every variant SFX call should wrap;
 *    it consults the Preferences store via `isMuted()` and no-ops when true.
 *
 * Reads mute state from `localStorage` (key: `rendever-games.prefs`). The
 * Preferences provider writes there; this module reads. We don't pull in a
 * React dependency for what is, fundamentally, an audio primitive.
 */

// ---------------------------------------------------------------------------
// AudioContext singleton + master gain
// ---------------------------------------------------------------------------

let ctxRef: AudioContext | null = null;
let masterRef: GainNode | null = null;

/**
 * Lazy AudioContext singleton. Returns `null` in SSR / non-browser environments.
 * Browsers require AudioContexts to be resumed from a user gesture — variants
 * should call this on first user input and connect their output to the master
 * gain returned by {@link getMasterGain}.
 */
export function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctxRef) return ctxRef;
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctxRef = new Ctor();
    masterRef = ctxRef.createGain();
    masterRef.gain.value = 0.6; // gentle headroom — SFX are background, not foreground
    masterRef.connect(ctxRef.destination);
    return ctxRef;
  } catch {
    return null;
  }
}

/**
 * The master gain node every variant sound should route into. Created on
 * first {@link getAudioContext} call. Returns `null` until then.
 */
export function getMasterGain(): GainNode | null {
  if (!ctxRef) {
    // Trigger lazy init so callers don't have to remember to call ctx first.
    getAudioContext();
  }
  return masterRef;
}

/**
 * Resume the AudioContext if it's suspended. Browsers auto-suspend until a
 * user gesture; call this from a known click/tap handler the first time.
 */
export function unlockAudio(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    void ctx.resume().catch(() => {
      /* ignore — best effort */
    });
  }
}

// ---------------------------------------------------------------------------
// Oscillator helpers
// ---------------------------------------------------------------------------

export type OscillatorType = 'sine' | 'square' | 'sawtooth' | 'triangle';

export interface MakeOscillatorOptions {
  freq: number;
  type?: OscillatorType;
  detune?: number;
}

/**
 * Create an OscillatorNode with the given frequency and waveform. Returned
 * node is NOT started — caller does `osc.start(t)` / `osc.stop(t + dur)`.
 */
export function makeOscillator(
  ctx: AudioContext,
  { freq, type = 'sine', detune = 0 }: MakeOscillatorOptions,
): OscillatorNode {
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ctx.currentTime);
  if (detune !== 0) osc.detune.setValueAtTime(detune, ctx.currentTime);
  return osc;
}

// ---------------------------------------------------------------------------
// Envelope shaper
// ---------------------------------------------------------------------------

export interface EnvelopeOptions {
  attack: number;
  decay?: number;
  sustain?: number;
  release: number;
  peak?: number;
}

/**
 * Returns a GainNode shaped with an ADSR envelope. Variants connect their
 * audio source into this node and route its output downstream.
 *
 * The envelope is scheduled from `ctx.currentTime`. Total duration =
 * `attack + decay + release` seconds. `sustain` is the level (0..1) between
 * attack-peak and the start of release; defaults to 0 (no hold).
 *
 * Why `exponentialRampToValueAtTime`: feels more musical than linear ramps.
 * Browsers reject ramps to exactly 0, so we ramp to a tiny epsilon (0.0001).
 */
export function envelope(
  ctx: AudioContext,
  opts: EnvelopeOptions,
): { node: GainNode; tEnd: number } {
  const peak = opts.peak ?? 0.5;
  const sustain = opts.sustain ?? 0;
  const decay = opts.decay ?? 0;
  const t0 = ctx.currentTime;
  const tAttack = t0 + opts.attack;
  const tDecay = tAttack + decay;
  const tRelease = tDecay + opts.release;

  const node = ctx.createGain();
  node.gain.cancelScheduledValues(t0);
  node.gain.setValueAtTime(0.0001, t0);
  node.gain.exponentialRampToValueAtTime(peak, tAttack);
  if (decay > 0) {
    node.gain.exponentialRampToValueAtTime(
      Math.max(0.0001, sustain * peak),
      tDecay,
    );
  }
  node.gain.exponentialRampToValueAtTime(0.0001, tRelease);

  return { node, tEnd: tRelease };
}

// ---------------------------------------------------------------------------
// Noise buffer
// ---------------------------------------------------------------------------

export interface NoiseBufferOptions {
  lengthMs: number;
  /** Optional bandpass center frequency. Applied via filter, not buffer math. */
  filterFreq?: number;
  /** Optional shaping power — values > 1 bias toward smaller-magnitude noise. */
  shaping?: number;
}

/**
 * Build an AudioBuffer containing white noise (optionally shaped). Variants
 * wrap this in a BufferSource and route through a filter to taste.
 */
export function noiseBuffer(
  ctx: AudioContext,
  { lengthMs, shaping = 1 }: NoiseBufferOptions,
): AudioBuffer {
  const length = Math.max(1, Math.floor(ctx.sampleRate * (lengthMs / 1000)));
  const buf = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) {
    const x = Math.random() * 2 - 1;
    if (shaping === 1) {
      data[i] = x;
    } else {
      data[i] = Math.sign(x) * Math.pow(Math.abs(x), shaping);
    }
  }
  return buf;
}

// ---------------------------------------------------------------------------
// Node chaining
// ---------------------------------------------------------------------------

/**
 * Chain an arbitrary list of AudioNodes by connecting each to the next.
 * Returns the last node so the caller can route it onward.
 *
 * Example: `connectThrough(source, filter, envelopeNode, masterGain)`.
 */
export function connectThrough<T extends AudioNode>(...nodes: AudioNode[]): T {
  for (let i = 0; i < nodes.length - 1; i += 1) {
    nodes[i]!.connect(nodes[i + 1]!);
  }
  return nodes[nodes.length - 1] as T;
}

// ---------------------------------------------------------------------------
// Mute gate
// ---------------------------------------------------------------------------

const PREFS_KEY = 'rendever-games.prefs';

interface StoredPrefs {
  theme?: 'light' | 'dark';
  muted?: boolean;
}

/**
 * Read the user's mute preference from localStorage. Returns `false` (audible)
 * when the prefs store is missing, malformed, or in a non-browser environment.
 *
 * Kept synchronous + dependency-free so audio code paths stay cheap.
 */
export function isMuted(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as StoredPrefs;
    return parsed.muted === true;
  } catch {
    return false;
  }
}

/**
 * Wrap a synthesis function so it no-ops when the user has muted audio.
 * Logs at debug level in dev so devs can see "this would have played X"
 * during a mute verification pass.
 *
 * Variants should funnel every SFX call through this wrapper:
 *   `playIfNotMuted('chime', () => synthesizeChime(ctx, master))`
 */
export function playIfNotMuted(name: string, fn: () => void): void {
  if (isMuted()) {
    if (import.meta.env.DEV) {
      console.debug(`[audio] muted, skipping "${name}"`);
    }
    return;
  }
  try {
    fn();
    if (import.meta.env.DEV) {
      console.debug(`[audio] play "${name}"`);
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn(`[audio] failed "${name}"`, err);
    }
  }
}
